<script setup lang="ts">
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import { CheckCircle2, ExternalLink } from 'lucide-vue-next'
import { computed, onMounted, reactive } from 'vue'

import AppAlert from '@/components/AppAlert.vue'
import FormField from '@/components/FormField.vue'
import SubRecordSection from '@/components/applications/SubRecordSection.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useGoogleAdsConversion } from '@/composables/useGoogleAdsConversion'
import { useRecaptcha } from '@/composables/useRecaptcha'
import LandingLayout from '@/layouts/LandingLayout.vue'
import { type ExpenseColumn } from '@/lib/constants'
import { formatLabel } from '@/lib/utils'
import { applyPageCompletePath, applyPagePath, newPortalSessionPath } from '@/routes'

interface Employment {
  employment_status: string
  industry: string
  employment_title: string
  employer_name: string
  employer_phone: string
  employers_address: string
  years_at_employment: string
  months_at_employment: string
}
interface Asset {
  name: string
  value: string
  description: string
}
interface Liability {
  asset_id: string
  liability_type: string
  lender: string
  amount: string
  repayment: string
  description: string
  will_consolidate: boolean
}
interface BankAccount {
  name: string
  account: string
  bsb: string
  balance: string
  account_type: string
}
type Expense = Record<ExpenseColumn, string>

const props = defineProps<{
  application_id: string
  existing: {
    current_employment: Record<string, unknown> | null
    previous_employment: Record<string, unknown> | null
    expense: Expense | null
    bank_accounts: BankAccount[]
    assets: Asset[]
    liabilities: Liability[]
  }
  employment_statuses: string[]
  employment_titles: string[]
  industries: string[]
  expense_columns: ExpenseColumn[]
  liability_types: string[]
  bank_account_types: string[]
  bank_names: string[]
  recaptcha_site_key?: string | null
  ads_conversion_id?: string | null
  ads_conversion_label?: string | null
}>()

const { getToken } = useRecaptcha(props.recaptcha_site_key)
const { fireConversion } = useGoogleAdsConversion(props.ads_conversion_id)

onMounted(() => {
  localStorage.setItem('tlc_application_id', props.application_id)
  if (props.ads_conversion_label) fireConversion(props.ads_conversion_label)
})

const toStr = (v: unknown) => (v == null ? '' : String(v))

const defaultEmployment = (): Employment => ({
  employment_status: '',
  industry: '',
  employment_title: '',
  employer_name: '',
  employer_phone: '',
  employers_address: '',
  years_at_employment: '',
  months_at_employment: '',
})

const initEmployment = (e: Record<string, unknown> | null): Employment =>
  e
    ? {
        employment_status: toStr(e.employment_status),
        industry: toStr(e.industry),
        employment_title: toStr(e.employment_title),
        employer_name: toStr(e.employer_name),
        employer_phone: toStr(e.employer_phone),
        employers_address: toStr(e.employers_address),
        years_at_employment: toStr(e.years_at_employment),
        months_at_employment: toStr(e.months_at_employment),
      }
    : defaultEmployment()

const currentEmployment = reactive<Employment>(initEmployment(props.existing.current_employment))
const previousEmployment = reactive<Employment>(initEmployment(props.existing.previous_employment))

const defaultExpense = () => Object.fromEntries(props.expense_columns.map((c) => [c, ''])) as Expense

const expense = reactive<Expense>(
  props.existing.expense
    ? (Object.fromEntries(props.expense_columns.map((c) => [c, toStr(props.existing.expense![c])])) as Expense)
    : defaultExpense(),
)

const expenseTotal = computed(() =>
  props.expense_columns.reduce((sum, col) => sum + (parseFloat(expense[col]) || 0), 0),
)

const bankAccounts = reactive<BankAccount[]>(
  props.existing.bank_accounts.length
    ? props.existing.bank_accounts.map((b) => ({ ...b, balance: toStr(b.balance) }))
    : [{ name: '', account: '', bsb: '', balance: '', account_type: '' }],
)

const assets = reactive<Asset[]>(
  props.existing.assets.length ? props.existing.assets.map((a) => ({ ...a, value: toStr(a.value) })) : [],
)

const liabilities = reactive<Liability[]>(
  props.existing.liabilities.length
    ? props.existing.liabilities.map((l) => ({
        ...l,
        amount: toStr(l.amount),
        repayment: toStr(l.repayment),
        asset_id: toStr(l.asset_id),
      }))
    : [],
)

const currentTotalMonths = computed(() => {
  const years = parseInt(currentEmployment.years_at_employment) || 0
  const months = parseInt(currentEmployment.months_at_employment) || 0
  return years * 12 + months
})

const needsPreviousEmployment = computed(
  () => currentEmployment.years_at_employment !== '' && currentTotalMonths.value < 24,
)

const validationErrors = computed(() => {
  const errors: string[] = []

  if (!currentEmployment.employment_status) errors.push('Current employment: status is required')
  if (!currentEmployment.employment_title) errors.push('Current employment: title is required')
  if (!currentEmployment.employer_name.trim()) errors.push('Current employment: employer name is required')
  if (currentEmployment.years_at_employment === '') errors.push('Current employment: years at employment is required')

  if (needsPreviousEmployment.value) {
    if (!previousEmployment.employment_status) errors.push('Previous employment: status is required')
    if (!previousEmployment.employment_title) errors.push('Previous employment: title is required')
    if (!previousEmployment.employer_name.trim()) errors.push('Previous employment: employer name is required')
    if (previousEmployment.years_at_employment === '')
      errors.push('Previous employment: years at employment is required')
  }

  props.expense_columns.forEach((col) => {
    if (expense[col] === '') errors.push(`Expenses: ${formatLabel(col)} is required`)
  })

  if (bankAccounts.length === 0) {
    errors.push('At least one bank account is required')
  } else {
    bankAccounts.forEach((b, i) => {
      if (!b.name.trim()) errors.push(`Bank account ${i + 1}: name is required`)
      if (!b.account.trim()) errors.push(`Bank account ${i + 1}: account number is required`)
      if (!b.account_type) errors.push(`Bank account ${i + 1}: account type is required`)
    })
  }

  liabilities.forEach((l, i) => {
    if (!l.liability_type) errors.push(`Liability ${i + 1}: type is required`)
    if (l.amount === '') errors.push(`Liability ${i + 1}: amount is required`)
    if (l.repayment === '') errors.push(`Liability ${i + 1}: repayment is required`)
  })

  return errors
})

const canSubmit = computed(() => validationErrors.value.length === 0 && !form.processing)

const page = usePage()
const backendErrors = computed((): string[] => {
  const e = (page.props as Record<string, unknown>).errors
  if (!e) return []
  if (Array.isArray(e)) return e as string[]
  if (typeof e === 'object' && Object.keys(e as object).length === 0) return []
  return [String(e)]
})

function addBankAccount() {
  bankAccounts.push({ name: '', account: '', bsb: '', balance: '', account_type: '' })
}
function addAsset() {
  assets.push({ name: '', value: '', description: '' })
}
function addLiability() {
  liabilities.push({
    asset_id: '',
    liability_type: '',
    lender: '',
    amount: '',
    repayment: '',
    description: '',
    will_consolidate: false,
  })
}
function liabilityAmountLabel(type: string) {
  return type === 'credit_card' ? 'Card Limit ($)' : 'Amount ($)'
}
function liabilityRepaymentLabel(type: string) {
  return type === 'credit_card' ? 'Owing ($)' : 'Monthly Repayment ($)'
}
function liabilityDescriptionLabel(type: string) {
  return type === 'bnpl' ? 'Limit ($)' : 'Description'
}

const form = useForm({})

function startFresh() {
  localStorage.removeItem('tlc_customer_id')
  localStorage.removeItem('tlc_application_id')
  router.visit(applyPagePath())
}

async function submit() {
  const recaptcha_token = await getToken('apply_step3')
  form
    .transform(() => ({
      application_id: props.application_id,
      employments: {
        current: currentEmployment,
        previous: needsPreviousEmployment.value ? previousEmployment : undefined,
      },
      expenses: [Object.fromEntries(props.expense_columns.map((c) => [c, expense[c] === '' ? '0' : expense[c]]))],
      bank_accounts: bankAccounts,
      assets,
      liabilities,
      recaptcha_token,
    }))
    .post(applyPageCompletePath())
}
</script>

<template>
  <Head title="Financial Details — Apply" />
  <LandingLayout>
    <!-- Application received banner -->
    <AppAlert variant="success" title="Application received!" class="mb-6 rounded-xl">
      <template #icon>
        <CheckCircle2 />
      </template>
      <p>
        Have time to complete all your info in full? Updating all your info now will allow the TLC team to
        accelerate your processing time! — but you can also come back to it later.
      </p>
      <a
        :href="newPortalSessionPath()"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-2 inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 hover:opacity-80"
      >
        Track your application in the portal
        <ExternalLink class="size-3.5" />
      </a>
    </AppAlert>

    <div class="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">Financial Details</h1>
        <p class="text-muted-foreground mt-1 text-sm">Step 3 of 3 — Employment, expenses & assets</p>
      </div>
      <button
        type="button"
        @click="startFresh"
        class="text-muted-foreground hover:text-foreground shrink-0 text-xs underline"
      >
        Not you? Start fresh
      </button>
    </div>

    <form @submit.prevent="submit" class="space-y-6">
      <!-- Current Employment -->
      <Card>
        <CardHeader><CardTitle class="text-base">Current Employment</CardTitle></CardHeader>
        <CardContent>
          <div class="space-y-3">
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <FormField label="Employment Status" required>
                <Select v-model="currentEmployment.employment_status">
                  <SelectTrigger class="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="s in props.employment_statuses" :key="s" :value="s">
                      {{ formatLabel(s) }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Employment Title" required>
                <Select v-model="currentEmployment.employment_title">
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select title" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="t in props.employment_titles" :key="t" :value="t">{{ t }}</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Employer Name" required>
                <Input v-model="currentEmployment.employer_name" placeholder="Employer name" />
              </FormField>
            </div>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <FormField label="Industry" class="sm:col-span-2">
                <Select v-model="currentEmployment.industry">
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="t in props.industries" :key="t" :value="t">{{ t.toUpperCase() }}</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Employer Phone">
                <Input v-model="currentEmployment.employer_phone" placeholder="Optional" />
              </FormField>
            </div>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <FormField label="Years at Employment" required>
                <Select v-model="currentEmployment.years_at_employment">
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select years" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="y in 6" :key="y - 1" :value="String(y - 1)">
                      {{ y - 1 === 5 ? '5+ years' : `${y - 1} ${y - 1 === 1 ? 'year' : 'years'}` }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Months at Employment">
                <Select v-model="currentEmployment.months_at_employment">
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select months" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="m in 12" :key="m - 1" :value="String(m - 1)">
                      {{ m - 1 === 1 ? '1 month' : `${m - 1} months` }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Employer Address">
                <Input v-model="currentEmployment.employers_address" placeholder="Optional" />
              </FormField>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Previous Employment (auto-shown when current < 2 years) -->
      <template v-if="needsPreviousEmployment">
        <AppAlert variant="warning">
          Your current employment is less than 2 years. Please provide your previous employment details.
        </AppAlert>
        <Card>
          <CardHeader><CardTitle class="text-base">Previous Employment</CardTitle></CardHeader>
          <CardContent>
            <div class="space-y-3">
              <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
                <FormField label="Employment Status" required>
                  <Select v-model="previousEmployment.employment_status">
                    <SelectTrigger class="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="s in props.employment_statuses" :key="s" :value="s">
                        {{ formatLabel(s) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employment Title" required>
                  <Select v-model="previousEmployment.employment_title">
                    <SelectTrigger class="w-full"><SelectValue placeholder="Select title" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="t in props.employment_titles" :key="t" :value="t">{{ t }}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employer Name" required>
                  <Input v-model="previousEmployment.employer_name" placeholder="Employer name" />
                </FormField>
              </div>
              <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
                <FormField label="Industry" class="sm:col-span-2">
                  <Select v-model="previousEmployment.industry">
                    <SelectTrigger class="w-full"><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="t in props.industries" :key="t" :value="t">{{ t.toUpperCase() }}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employer Phone">
                  <Input v-model="previousEmployment.employer_phone" placeholder="Optional" />
                </FormField>
              </div>
              <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
                <FormField label="Years at Employment" required>
                  <Select v-model="previousEmployment.years_at_employment">
                    <SelectTrigger class="w-full"><SelectValue placeholder="Select years" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="y in 6" :key="y - 1" :value="String(y - 1)">
                        {{ y - 1 === 5 ? '5+ years' : `${y - 1} ${y - 1 === 1 ? 'year' : 'years'}` }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Months at Employment">
                  <Select v-model="previousEmployment.months_at_employment">
                    <SelectTrigger class="w-full"><SelectValue placeholder="Select months" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="m in 12" :key="m - 1" :value="String(m - 1)">
                        {{ m - 1 === 1 ? '1 month' : `${m - 1} months` }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employer Address">
                  <Input v-model="previousEmployment.employers_address" placeholder="Optional" />
                </FormField>
              </div>
            </div>
          </CardContent>
        </Card>
      </template>

      <!-- Expenses (single record) -->
      <Card>
        <CardHeader><CardTitle class="text-base">Monthly Expenses</CardTitle></CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 items-start gap-3 sm:grid-cols-3">
            <div v-for="col in props.expense_columns" :key="col" class="grid gap-2">
              <Label :for="`expense_${col}`">{{ formatLabel(col) }} ($/mo)</Label>
              <Input
                :id="`expense_${col}`"
                :name="`expense_${col}`"
                v-model="expense[col]"
                type="number"
                min="0"
                placeholder="0"
              />
            </div>
          </div>
          <div class="border-border mt-4 flex items-center gap-3 border-t pt-4">
            <Label class="text-muted-foreground text-sm">Total monthly expenses</Label>
            <div class="bg-muted text-foreground rounded-md border px-3 py-1.5 text-sm font-medium tabular-nums">
              ${{ expenseTotal.toFixed(2) }}
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Bank Accounts -->
      <SubRecordSection
        title="Bank Accounts"
        :items="bankAccounts"
        @add="addBankAccount"
        @remove="(i) => bankAccounts.splice(i, 1)"
      >
        <template #default="{ index }">
          <div class="space-y-3">
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <div class="grid gap-2">
                <Label :for="`bank_name_${index}`">Bank Name <span class="text-destructive">*</span></Label>
                <Select :id="`bank_name_${index}`" v-model="bankAccounts[index].name" required>
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="b in props.bank_names" :key="b" :value="b">{{ b }}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div class="grid gap-2">
                <Label :for="`bank_account_type_${index}`">Account Type <span class="text-destructive">*</span></Label>
                <Select :id="`bank_account_type_${index}`" v-model="bankAccounts[index].account_type" required>
                  <SelectTrigger class="w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="t in props.bank_account_types" :key="t" :value="t">
                      {{ formatLabel(t) }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <div class="grid gap-2">
                <Label :for="`bank_account_${index}`">Account Number <span class="text-destructive">*</span></Label>
                <Input
                  :id="`bank_account_${index}`"
                  :name="`bank_account_${index}`"
                  v-model="bankAccounts[index].account"
                  placeholder="123456789"
                  required
                />
              </div>
              <div class="grid gap-2">
                <Label :for="`bank_bsb_${index}`">BSB</Label>
                <Input
                  :id="`bank_bsb_${index}`"
                  :name="`bank_bsb_${index}`"
                  v-model="bankAccounts[index].bsb"
                  placeholder="062000"
                />
              </div>
              <div class="grid gap-2">
                <Label :for="`bank_balance_${index}`">Balance ($)</Label>
                <Input
                  :id="`bank_balance_${index}`"
                  :name="`bank_balance_${index}`"
                  v-model="bankAccounts[index].balance"
                  type="number"
                  min="0"
                />
              </div>
            </div>
          </div>
        </template>
      </SubRecordSection>

      <!-- Assets -->
      <SubRecordSection title="Assets" :items="assets" @add="addAsset" @remove="(i) => assets.splice(i, 1)">
        <template #default="{ index }">
          <div class="space-y-3">
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <div class="grid gap-2">
                <Label :for="`asset_name_${index}`">Asset Name <span class="text-destructive">*</span></Label>
                <Input
                  :id="`asset_name_${index}`"
                  :name="`asset_name_${index}`"
                  v-model="assets[index].name"
                  placeholder="e.g. My Car, Home Equity"
                  required
                />
              </div>
              <div class="grid gap-2">
                <Label :for="`asset_value_${index}`">Value ($) <span class="text-destructive">*</span></Label>
                <Input
                  :id="`asset_value_${index}`"
                  :name="`asset_value_${index}`"
                  v-model="assets[index].value"
                  type="number"
                  min="0"
                  required
                />
              </div>
            </div>
            <div class="grid gap-2">
              <Label :for="`asset_description_${index}`">Description</Label>
              <Input
                :id="`asset_description_${index}`"
                :name="`asset_description_${index}`"
                v-model="assets[index].description"
                placeholder="Optional"
              />
            </div>
          </div>
        </template>
      </SubRecordSection>

      <!-- Liabilities -->
      <SubRecordSection
        title="Liabilities"
        :items="liabilities"
        @add="addLiability"
        @remove="(i) => liabilities.splice(i, 1)"
      >
        <template #default="{ index }">
          <div class="space-y-3">
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <div class="grid gap-2">
                <Label :for="`liability_type_${index}`">Liability Type <span class="text-destructive">*</span></Label>
                <Select v-model="liabilities[index].liability_type">
                  <SelectTrigger :id="`liability_type_${index}`" class="w-full">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="t in props.liability_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div class="grid gap-2">
                <Label :for="`liability_lender_${index}`">Lender</Label>
                <Input
                  :id="`liability_lender_${index}`"
                  :name="`liability_lender_${index}`"
                  v-model="liabilities[index].lender"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <div class="grid gap-2">
                <Label :for="`liability_amount_${index}`"
                  >{{ liabilityAmountLabel(liabilities[index].liability_type) }}
                  <span class="text-destructive">*</span></Label
                >
                <Input
                  :id="`liability_amount_${index}`"
                  :name="`liability_amount_${index}`"
                  v-model="liabilities[index].amount"
                  type="number"
                  min="0"
                  required
                />
              </div>
              <div class="grid gap-2">
                <Label :for="`liability_repayment_${index}`"
                  >{{ liabilityRepaymentLabel(liabilities[index].liability_type) }}
                  <span class="text-destructive">*</span></Label
                >
                <Input
                  :id="`liability_repayment_${index}`"
                  :name="`liability_repayment_${index}`"
                  v-model="liabilities[index].repayment"
                  type="number"
                  min="0"
                  required
                />
              </div>
            </div>
            <div class="grid gap-2" :class="liabilities[index].liability_type === 'bnpl' && 'sm:max-w-xs'">
              <Label :for="`liability_description_${index}`">{{
                liabilityDescriptionLabel(liabilities[index].liability_type)
              }}</Label>
              <Input
                :id="`liability_description_${index}`"
                :name="`liability_description_${index}`"
                v-model="liabilities[index].description"
                :type="liabilities[index].liability_type === 'bnpl' ? 'number' : 'text'"
                :min="liabilities[index].liability_type === 'bnpl' ? '0' : undefined"
                placeholder="Optional"
              />
            </div>
            <div class="flex items-center gap-2">
              <Checkbox
                :id="`consolidate_${index}`"
                :model-value="liabilities[index].will_consolidate"
                class="size-4"
                @update:model-value="(v) => (liabilities[index].will_consolidate = !!v)"
              />
              <Label :for="`consolidate_${index}`">Will Consolidate into this loan</Label>
            </div>
          </div>
        </template>
      </SubRecordSection>

      <div v-if="backendErrors.length" class="border-destructive/30 bg-destructive/5 space-y-1 rounded-lg border p-3">
        <p v-for="err in backendErrors" :key="err" class="text-destructive text-sm">{{ err }}</p>
      </div>

      <div
        v-if="validationErrors.length"
        class="border-destructive/30 bg-destructive/5 space-y-1 rounded-lg border p-3"
      >
        <p v-for="err in validationErrors" :key="err" class="text-destructive text-sm">{{ err }}</p>
      </div>

      <Button type="submit" class="w-full" :disabled="!canSubmit"> Submit Application </Button>
    </form>
  </LandingLayout>
</template>
